import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { mgcIconLockRegular } from '@sb/mgc-icons/mgcIcon-lock_regular.icon';
import { ButtonComponent } from '@sb/ui/button';
import { IconRegistry } from '@sb/ui/icon';
import {
  SidebarAccordionBodyComponent,
  SidebarAccordionItemDirective,
  SidebarComponent,
  SidebarItemComponent,
  SidebarListDirective,
} from '@sb/ui/navigation';

import { SidebarMenuItem } from './sidebar.model';

@Component({
  selector: 'account-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [
    TranslatePipe,
    SidebarComponent,
    SidebarListDirective,
    SidebarItemComponent,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    SidebarAccordionItemDirective,
    SidebarAccordionBodyComponent,
    ButtonComponent,
  ],
})
export class AccountSidebarComponent {
  #iconRegistry = inject(IconRegistry);

  title = input('');
  menuItems = input<SidebarMenuItem[]>([]);
  selectedItem = input<SidebarMenuItem | null>(null);
  showChargebeeTestButtons = input(false);

  lockedItemClick = output<SidebarMenuItem>();
  importChargeBee = output();
  pushChargeBee = output();

  constructor() {
    this.#iconRegistry.registerIcons([mgcIconLockRegular]);
  }

  groupIsExpanded(item: SidebarMenuItem): boolean {
    if (item.state?.isLocked) {
      return false;
    }

    return this.#isSelected(item) || !!item.items?.some((subItem) => this.#isSelected(subItem));
  }

  // The menu tree is rebuilt from scratch whenever account state changes, so the selected
  // item is rarely the same object as the one being rendered — the route is the only
  // stable identity these two share.
  #isSelected(item: SidebarMenuItem): boolean {
    const selectedRouterLink = this.selectedItem()?.routerLink;

    return !!selectedRouterLink && item.routerLink === selectedRouterLink;
  }

  getGroupItemLink(item: SidebarMenuItem): [string] | null {
    const firstNonLockedSubItem = item.items?.find((subItem) => !subItem.state?.isLocked);

    return item.state?.isLocked || !firstNonLockedSubItem ? null : [firstNonLockedSubItem.routerLink];
  }

  onItemClick(item: SidebarMenuItem) {
    if (item?.state?.isLocked && item?.state?.lockType) {
      this.lockedItemClick.emit(item);
      return;
    }
  }

  onImportChargeBee() {
    this.importChargeBee.emit();
  }

  onPushChargeBee() {
    this.pushChargeBee.emit();
  }

  toSidebarItem(item: unknown): SidebarMenuItem {
    return item as SidebarMenuItem;
  }
}
